import moment, { Moment } from 'moment';
import React, { FC, useState, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';

import { ReactDateTime, ReactDateTimeComponent } from './ReactDateTime';

import { IconButtonDeprecated } from '../deprecated/IconButtonDeprecated';
import { Input } from '../Input';

import { append } from '../../utils';
import { CosmosComponent } from '../../types';

import 'react-datetime/css/react-datetime.css';

export interface DateTimePickerProps extends CosmosComponent {
  inputClassName?: string;
  clearButtonClassName?: string;
  isClearable?: boolean;
  initialValue?: Moment;
  align?: 'left' | 'right';
  renderInput?: FC<typeof ReactDateTimeComponent & { calendarUTCTime: string }>;
  onClearSelection?: () => void;
  onDateSelected?: (date: Moment) => void;
  allowCustomInput?: boolean;
  hidePlaceHolder?: boolean;
  value?: Moment;
}

const locales = window.navigator['userLanguage'] || window.navigator.language;

const dateFormat = (locales) => {
  switch (locales) {
    case 'en-AU':
    case 'en-NZ': {
      return 'ddd D MMM, YYYY';
    }
    default:
      return 'ddd MMM D, YYYY';
  }
};

export const defaultDateFormat = dateFormat(locales);

export const DateTimePicker: FC<DateTimePickerProps & ReactDateTime> = ({
  closeOnSelect = true,
  dateFormat = defaultDateFormat,
  timeFormat = false,
  align = 'left',
  initialValue,
  icon,
  isInverted,
  isClearable,
  inputClassName,
  clearButtonClassName,
  onDateSelected,
  onClearSelection,
  testId,
  value,
  renderInput,
  allowCustomInput,
  hidePlaceHolder = false, // prop to hide placeHolder
  ...restProps
}) => {
  const startOfTheMonth = moment().startOf('month');

  const finalDateFormat =
    typeof dateFormat === 'string' ? dateFormat : defaultDateFormat;
  const formattedInitialValue = initialValue
    ? initialValue.format(finalDateFormat)
    : '';

  const [inputTime, setInputTime] = useState(formattedInitialValue);
  const [inputValue, setInputValue] = useState(formattedInitialValue);
  const [calendarTime, setCalendarTime] = useState(
    initialValue || startOfTheMonth,
  );

  const localDateFormat = moment.localeData(locales).longDateFormat('L');

  const onClearClick = () => {
    setInputTime('');
    setInputValue('');
    setCalendarTime(startOfTheMonth);
    if (onClearSelection) {
      onClearSelection();
    }
  };

  const setStateValues = useCallback(
    (date: Moment) => {
      setInputTime(date.format(finalDateFormat));
      setInputValue(date.format(finalDateFormat));
      setCalendarTime(date);
    },
    [finalDateFormat],
  );

  const onDateClick = useCallback(
    (date: Moment) => {
      setStateValues(date);
      onDateSelected?.(date);
    },
    [onDateSelected, setStateValues],
  );

  useEffect(() => {
    if (moment.isMoment(value)) {
      setStateValues(value);
    }
  }, [value, setStateValues]);

  const handleOnChange = (date) => {
    setInputValue(date);
    setInputTime('');
    setCalendarTime(startOfTheMonth);
  };

  const setDate = (date) => {
    setInputTime(date.format(finalDateFormat));
    setInputValue(date.format(finalDateFormat));
    setCalendarTime(date);
    onDateSelected && onDateSelected(date);
  };

  const handleOnInputChange = (event) => {
    const globalFormatDate = moment(inputValue);
    if (event.keyCode === 13 || event.keyCode === 9) {
      const validDate = moment(inputValue, localDateFormat);
      // Only if local date format is `DD/MM/YYYY`, blocking `MM/DD/YYYY` format,
      // else it'll work as it is.
      const notAllowedFormat =
        localDateFormat === 'DD/MM/YYYY' &&
        moment(inputValue, 'MM/DD/YYYY').isValid();
      if (!validDate.isValid() && !globalFormatDate.isValid()) {
        onClearClick();
        return false;
      }
      const getValidDate = () =>
        inputValue.includes('/') && notAllowedFormat
          ? onClearClick()
          : globalFormatDate;
      const setDateValue = validDate.isValid() ? validDate : getValidDate();
      setDateValue && setDateValue.isValid() && setDate(setDateValue);
    }
  };

  const handleFocusEvent = (event) => {
    event.target.select();
  };

  const handleBlurEvent = () => {
    if (inputValue === '') {
      onClearSelection?.();
    }
  };

  const customInputProps = {
    onInputChange: handleOnChange,
    onKeyDown: handleOnInputChange,
    onFocus: handleFocusEvent,
    onBlur: handleBlurEvent,
    placeholder: !hidePlaceHolder ? localDateFormat : '',
  };

  const defaultRenderInput = (restInputProps) => (
    <StyledDateTimePickerContent>
      <div className={inputClassName}>
        <Input
          {...restInputProps}
          autoComplete="off"
          icon={icon}
          isInverted={isInverted}
          value={inputValue}
          testId={append(testId, 'input')}
          {...(allowCustomInput && customInputProps)}
        />
      </div>

      {isClearable && inputTime && (
        <StyledIconButton
          className={clearButtonClassName}
          name="cross"
          onClick={onClearClick}
          testId={append(testId, 'clear')}
        />
      )}
    </StyledDateTimePickerContent>
  );

  const enhancedRenderInput = renderInput
    ? (props) =>
        renderInput({ ...props, calendarUTCTime: calendarTime.utc().format() })
    : null;

  const handleRenderInput = enhancedRenderInput || defaultRenderInput;

  return (
    <span data-test-id={testId}>
      <StyledDateTimePicker
        {...restProps}
        align={align}
        timeFormat={timeFormat}
        dateFormat={dateFormat}
        closeOnSelect={closeOnSelect}
        initialViewDate={calendarTime}
        onChange={onDateClick}
        value={calendarTime}
        renderInput={handleRenderInput}
      />
    </span>
  );
};

const StyledDateTimePicker = styled(ReactDateTimeComponent)<{ align: string }>`
  font-family: 'Open Sans', sans-serif;
  font-size: 1em;

  & input {
    cursor: pointer;
  }

  ${({ align }) =>
    align === 'right' &&
    css`
      & > :last-child {
        right: 0;
      }
    `}
`;

const StyledDateTimePickerContent = styled.div`
  display: flex;
  align-items: center;
`;

const StyledIconButton = styled(IconButtonDeprecated)`
  margin-left: 10px;
  height: 1.8rem;
  width: 1.8rem;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover,
  &:focus {
    background: ${({ theme: { colors } }) => colors?.borderLightgray};
    cursor: pointer;
  }

  & svg {
    width: 12px;
  }
`;
